import { HttpResponse, authDaoFactory } from '../../common'
import { errorUtil } from '../../util/auth-error-util'

export type AuthDaoLoginResult = {
  accessToken: string
  refreshToken: string
  redirectUrl: string
}

export type AuthDaoRegisterResult = {
  accessToken: string
  refreshToken: string
  redirectUrl: string
}

export type AuthDaoGoogleSignInResult = {
  accessToken: string
  refreshToken: string
}

export type User = {
  id: string
  email: string
  name: string
  phoneNumber?: string
  isEmailVerified: boolean
  isPhoneNumberVerified: boolean
  roles?: { name: string }[]
  isInactive: boolean
}

const dao = authDaoFactory({
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})
export const authDao = {
  ...dao,
  getUsers: async (): Promise<User[]> => {
    const result = await dao._http.get<HttpResponse<User>>('/users')
    return result.data.data
  },
  partialUpdateUser: async (params: { userId: string; data: Partial<User> }): Promise<User> => {
    const { userId, data } = params
    const result = await dao._http.patch<HttpResponse<User>>(`/users/${userId}`, data)
    return result.data.data[0]
  },
  requestEmailVerification: async (params: { userId: string; clientGuid: string }): Promise<void> => {
    const { userId, clientGuid } = params
    await dao._http.post<HttpResponse<void>>('/verify-email', { userId, clientGuid })
  },
  updateUserNameAndLegals: async (params: { name: string; acceptAllLegals?: boolean }): Promise<void> => {
    const { name, acceptAllLegals = false } = params
    await dao._http.patch<HttpResponse<User>>('/users/me', { name }, { params: { acceptAllLegals } })
  },
  agreeToLatestLegalAgreements: async (): Promise<void> => {
    const url = '/users/me/legal-agreements/latest'
    await dao._http.post(url)
  },
  getUserMe: async (): Promise<User> => {
    const url = `/users/me`
    const result = await dao._http.get<HttpResponse<User>>(url)
    return result.data.data[0]
  },
  signInWithApple: async (params: { identityToken: string; clientGuid: string }): Promise<AuthDaoGoogleSignInResult> => {
    const result = await dao._http.post<HttpResponse<AuthDaoGoogleSignInResult>>('/apple', {
      identityToken: params.identityToken,
      clientGuid: params.clientGuid,
    })
    return result.data.data[0]
  },
  signInWithGoogle: async (params: { idToken: string; clientGuid: string }): Promise<AuthDaoGoogleSignInResult> => {
    const result = await dao._http.post<HttpResponse<AuthDaoGoogleSignInResult>>('/google/callback', {
      idToken: params.idToken,
      clientGuid: params.clientGuid,
    })
    return result.data.data[0]
  },
  exchangeTempCode: async (params: { code: string }): Promise<AuthDaoLoginResult & { redirectUrl: string }> => {
    const result = await dao._http.post<HttpResponse<AuthDaoLoginResult & { redirectUrl: string }>>('/exchange-temp-code', {
      code: params.code,
    })
    return result.data.data[0]
  },
  requestPasswordlessLogin: async (params: { email: string; captchaToken?: string }): Promise<{ expireDate: number }> => {
    const result = await dao._http.post<HttpResponse<{ expireDate: number }>>('/passwordless/request', params, {
      headers: { 'X-Client-Platform': 'web' },
    })
    return result.data.data[0]
  },
  requestPasswordlessLoginMobile: async (params: { email: string }): Promise<{ expireDate: number }> => {
    const result = await dao._http.post<HttpResponse<{ expireDate: number }>>('/passwordless/request', params)
    return result.data.data[0]
  },
  verifyPasswordlessLogin: async (params: { email: string; code: string; clientGuid: string }): Promise<AuthDaoLoginResult> => {
    const result = await dao._http.post<HttpResponse<AuthDaoLoginResult>>('/passwordless/verify', params)
    return result.data.data[0]
  },
  resendPasswordlessCode: async (params: { email: string }): Promise<{ expireDate: number }> => {
    const result = await dao._http.post<HttpResponse<{ expireDate: number }>>('/passwordless/resend', params, {
      headers: { 'X-Client-Platform': 'web' },
    })
    return result.data.data[0]
  },
}
